import {
  Box,
  Typography,
  Button,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
  useScrollTrigger,
  Slide,
} from "@mui/material";
import { useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger({
    target: window,
    threshold: 100,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

export default function HeaderLandingComponent({ theme }) {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const navigationItems = [
    { label: "Início", id: "mainFrame" },
    { label: "Funcionalidades", id: "features" },
    { label: "Benefícios", id: "benefits" },
    { label: "Preços", id: "pricing" },
    { label: "Contato", id: "contact" },
  ];

  const toggleDrawer = (open) => {
    setDrawerOpen(open);
  };

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
      toggleDrawer(false);
    }
  };

  const goToLogin = () => {
    window.open("/login", "_blank");
  };

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => toggleDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100vw",
            height: "100vh",
            background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
            color: "white",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          },
        }}
      >
        <IconButton
          onClick={() => toggleDrawer(false)}
          sx={{
            position: "absolute",
            top: 24,
            right: 24,
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 32 }} />
        </IconButton>

        <Box
          sx={{
            textAlign: "center",
            gap: 4,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {navigationItems.map((item, index) => (
            <Typography
              key={index}
              variant="h4"
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateX(10px)",
                  opacity: 0.8,
                },
              }}
              onClick={() => handleScroll(item.id)}
            >
              {item.label}
            </Typography>
          ))}

          <Button
            onClick={goToLogin}
            variant="outlined"
            size="large"
            sx={{
              mt: 4,
              borderColor: "white",
              color: "white",
              borderWidth: 2,
              fontSize: "1.25rem",
              fontWeight: 600,
              padding: "16px 32px",
              "&:hover": {
                borderColor: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderWidth: 2,
              },
            }}
          >
            Acessar Plataforma
          </Button>
        </Box>
      </Drawer>

      {/* Header */}
      <HideOnScroll>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderBottom: "1px solid rgba(255, 115, 35, 0.1)",
            zIndex: 1200,
            transition: "all 0.3s ease",
          }}
        >
          <Toolbar
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              padding: { xs: "12px 24px", md: "16px 48px" },
              minHeight: { xs: "70px", md: "80px" },
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src={require("../../../assets/wala.png")}
              alt="Wala Smart Work"
              sx={{
                height: { xs: 45, md: 55 },
                width: "auto",
                cursor: "pointer",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
              onClick={() => handleScroll("mainFrame")}
            />

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 4,
              }}
            >
              {navigationItems.map((item, index) => (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    cursor: "pointer",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    transition: "all 0.3s ease",
                    position: "relative",
                    "&:hover": {
                      color: theme.palette.primary.main,
                      backgroundColor: "rgba(255, 115, 35, 0.05)",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: "50%",
                      width: 0,
                      height: 2,
                      backgroundColor: theme.palette.primary.main,
                      transition: "all 0.3s ease",
                      transform: "translateX(-50%)",
                    },
                    "&:hover::after": {
                      width: "80%",
                    },
                  }}
                  onClick={() => handleScroll(item.id)}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>

            {/* CTA Button */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                onClick={goToLogin}
                variant="contained"
                size="large"
                sx={{
                  background:
                    "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  padding: { xs: "10px 20px", md: "12px 24px" },
                  borderRadius: "12px",
                  boxShadow: "0px 4px 15px rgba(255, 115, 35, 0.3)",
                  display: { xs: "none", sm: "flex" },
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #D85A00 0%, #B84700 100%)",
                    transform: "translateY(-2px)",
                    boxShadow: "0px 8px 25px rgba(255, 115, 35, 0.4)",
                  },
                }}
              >
                Acessar Plataforma
              </Button>

              {/* Mobile Menu Button */}
              <IconButton
                onClick={() => toggleDrawer(true)}
                sx={{
                  display: { xs: "flex", md: "none" },
                  color: theme.palette.primary.main,
                  backgroundColor: "rgba(255, 115, 35, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 115, 35, 0.2)",
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      {/* Spacer for fixed header */}
      <Box sx={{ height: { xs: "70px", md: "80px" } }} />
    </>
  );
}
